import type { PlaceMatchCandidateDto } from '@upme/contracts';
import { Button } from '@upme/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@upme/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@upme/ui/components/popover';
import { cn } from '@upme/ui/lib/utils';
import { Check, ChevronsUpDown, MapPin, Tag, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminPlace, useAdminPlaces, usePlaceMatchQuery } from '../../places/api/places.queries';

interface Props {
  cityId: string;
  value: string | null;
  onChange: (placeId: string | null) => void;
  initialQuery: string;
}

export function PlaceMatchCombobox({ cityId, value, onChange, initialQuery }: Props) {
  const { t } = useTranslation('import-queue');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const matchQuery = usePlaceMatchQuery({ cityId, q: initialQuery });
  const placesQuery = useAdminPlaces(
    cityId ? { cityId, search: search || undefined, isActive: true, limit: 20 } : {},
    { enabled: !!cityId && open },
  );
  const selectedPlace = useAdminPlace(value ?? undefined, { enabled: !!value });

  // Auto-link top1 if matcher's autoLinkPlaceId is set and we don't already have a value.
  useEffect(() => {
    if (value == null && matchQuery.data?.autoLinkPlaceId) {
      onChange(matchQuery.data.autoLinkPlaceId);
    }
  }, [matchQuery.data?.autoLinkPlaceId, value, onChange]);

  // Reset selection when cityId changes (e.g. user switches city in the parent form).
  const cityIdRef = useRef(cityId);
  useEffect(() => {
    if (cityIdRef.current !== cityId) {
      cityIdRef.current = cityId;
      onChange(null);
    }
  }, [cityId, onChange]);

  const items = placesQuery.data?.items ?? [];
  const candidates = matchQuery.data?.candidates ?? [];
  const triggerLabel = selectedPlace.data
    ? `${selectedPlace.data.name} — ${selectedPlace.data.addressText}`
    : t('placeMatch.selectPlaceholder');

  function pick(id: string) {
    onChange(id);
    setOpen(false);
    setSearch('');
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" className="w-full justify-between">
            <span className="truncate text-left">{triggerLabel}</span>
            <span className="ml-2 flex items-center gap-1 shrink-0">
              {value && (
                <X
                  className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                  onClick={clear}
                  aria-label={t('placeMatch.unlink')}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[320px]">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('placeMatch.searchPlaceholder')}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>{t('placeMatch.notFound')}</CommandEmpty>
              <CommandGroup>
                {items.map((p) => (
                  <CommandItem key={p.id} value={p.id} onSelect={() => pick(p.id)}>
                    <Check className={cn('mr-2 h-4 w-4', value === p.id ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{p.name}</span>
                    <span className="ml-auto text-xs text-muted-foreground truncate max-w-[120px]">
                      {p.addressText}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {candidates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {candidates.slice(0, 3).map((c) => (
            <CandidateChip
              key={c.id}
              candidate={c}
              isSelected={c.id === value}
              onClick={() => onChange(c.id)}
            />
          ))}
        </div>
      )}

      {matchQuery.data && candidates.length === 0 && initialQuery.trim().length >= 3 && (
        <p className="text-xs text-muted-foreground">
          {t('placeMatch.noMatches')}{' '}
          <a href="/places/new" target="_blank" rel="noreferrer" className="underline">
            {t('placeMatch.createNew')}
          </a>
        </p>
      )}
    </div>
  );
}

function CandidateChip({
  candidate,
  isSelected,
  onClick,
}: {
  candidate: PlaceMatchCandidateDto;
  isSelected: boolean;
  onClick: () => void;
}) {
  const Icon = candidate.matchedField === 'name' ? Tag : MapPin;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition',
        isSelected ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted',
      )}
      title={candidate.addressText}
    >
      <Icon className="h-3 w-3 opacity-70" />
      <span className="truncate max-w-[140px]">{candidate.name}</span>
      <span className="text-[10px] opacity-70">({Math.round(candidate.similarity * 100)}%)</span>
    </button>
  );
}
